import { lowerFirstLetter } from '../utils/lowerFirstLetter'

// A locked affordance names the cheapest plan that unlocks it: Seasons is
// included in Standard, Doors and custom attributes are Premium-only.
const UPGRADE_TO = {
  standard: 'Upgrade to Standard',
  premium: 'Upgrade to Premium',
} as const

// Said to an account whose plan name cannot name the next step: a running
// preview reads as Premium, so "Upgrade to Premium" would be nonsense. Shared
// so the Plan card and the preview-ending banner can never disagree.
const UPGRADE_NOW = 'Upgrade Now'

const PREVIEW_CTA = {
  startPreview: 'Start full access preview',
  exploreSubscriptions: 'Explore Subscriptions',
} as const

// The name of a package, without any surrounding wording. Every surface that
// names the account's plan spells it from here, so the settings panel and the
// header pill can never disagree.
const PLAN_NAMES = {
  free: 'Free',
  standard: 'Standard',
  premium: 'Premium',
  // A running preview activates the Premium package, so the plan alone reads
  // as a paid Premium account. Both surfaces that name the plan say this
  // instead for as long as the preview lasts.
  premiumTrial: 'Premium Trial',
} as const

export const messages = {
  card: {
    delivery: 'Delivery:',
    category: 'Category:',
    cost: 'Cost:',
    retail: 'Retail:',
    qty: 'Units Ordered:',
  },
  attribution: {
    season: 'Season:',
    door: 'Door:',
    customAttributes: 'Custom Attributes:',
    emptyState: {
      defineLink: 'Define attributes',
      assingHighlight: 'assign existing attributes',
      or: ' or ',
      rest: ' to this product.',
    },
    removeAttribute: 'Remove attribute',
  },
  // A gated action's tooltip names the capability and shows a padlock, nothing
  // more — so these read as the tool's name rather than as a sentence.
  lockedAction: {
    capabilities: {
      budgets: 'Budgets',
      attribution: 'Attribution',
      doors: 'Doors',
      seasons: 'Seasons',
      filters: 'Filters',
      customProducts: 'Import products from non-JOOR orders',
      productDetailEditing: 'Edit products',
      secondGroupingLevel: 'Then by',
      exports: 'Download Assortment',
    },
    upgradeTo: UPGRADE_TO,
  },
  planNames: PLAN_NAMES,
  header: {
    title: 'Visual Assortment',
    empty: '',
    loadingProducts: 'Loading products',
    addingOrders: 'Adding orders',
    syncingOrders: 'Syncing orders',
    groupingProducts: 'Grouping products',
    budgetsTooltip: 'Budget',
    bulkEditing: 'Updating products',
    addProductsTooltip: 'Add products',
    uploadProductsDisabledTooltip: 'Disabled while uploading products',
    uploadSuccess: 'Products Added',
    uploadError: {
      prefix: 'Error adding products.',
      action: 'View status',
    },
    pendingUpdate: {
      prefix: 'Updates to visual assortment found. Screen will',
      action: 'refresh',
    },
    uploadingCustomProducts: {
      prefix: 'Adding products. Page will automatically refresh.',
      action: 'View status',
    },
    downloadAssortment: 'Download Assortment',
    download: 'Download',
    comingSoon: 'Coming Soon',
    planPillLabel: (planName: string) => `${planName} Plan`,
  },
  itemDetailPanel: {
    wholesaleCost: 'Product Cost:',
    retailPrice: 'Product Price:',
    orderNumber: 'Order Number:',
    orderStatus: 'Order Status:',
    orderDeliveryWindow: 'Order Delivery Window:',
    orderCategory: 'Order Category:',
    orderCost: 'Order Cost:',
    orderRetail: 'Order Retail:',
    orderColor: 'Order Color:',
    orderQty: 'Order Quantity:',
    orderMsg: 'Note: Order information cannot be edited.',
    received: 'Mark as Received',
    loading: 'Loading order/s...',
    error: 'Error loading order/s',
    placeholders: {
      additionalInfo: 'Additional Information...',
      productNumber: 'Product Number...',
      productCost: 'Product Cost...',
      productRetail: 'Product Retail...',
      brandName: 'Brand Name...',
      brandSearch: 'Search or add brand...',
      notAvailable: 'N/A',
    },
    createBrand: 'Create',
    groupedFieldTooltip:
      'Editing this field will cause the product to change groups based on the current grouping.',
    additionalInfo: 'Additional Information:',
    editSuccess: {
      title: 'Changes applied',
      description: 'Changes have been successfully applied',
    },
    editError: {
      title: 'Error applying changes',
      description: 'Error applying changes',
    },
    removeAttributeSuccess: {
      title: 'Attribute removed',
      description: 'The attribute has been removed from the item',
    },
    removeAttributeError: {
      title: 'Error removing attribute',
      description: 'The attribute could not be removed. Please try again.',
    },
    customAttributes: {
      name: 'Custom Attrs',
      label: 'Custom Attrs:',
      placeholder: 'Unassigned Attrs',
      noValues: 'No values yet',
      unassigned: 'Unassigned Custom Attrs',
      define: 'Define Custom Attrs',
      upgrade: UPGRADE_TO.premium,
    },
  },
  main: {
    emptyTitle: 'Add products to get started',
    emptySubtitle: 'Select an option in the panel',
    emptyFiltersResult: 'No results found',
    emptyFiltersResultText: 'Please, change your filters or',
    clearFilters: 'clear the filters.',
    errorTitle: 'Error Loading Styles',
    displayCap: {
      title: (remaining: number) =>
        `${remaining} more products in this assortment`,
      description: (shown: number) =>
        `The Free plan shows the first ${shown}. Nothing is lost — every product you send here is stored and counted, and a paid plan or your preview shows all of them.`,
      ...PREVIEW_CTA,
    },
  },
  // The offer above the header, shown only while the account still has its
  // one-time preview to spend.
  previewOffer: {
    errors: {
      startPreview: {
        title: 'Preview could not be started',
        description: 'Please try again in a moment.',
      },
    },
    title: (days: number) =>
      `You have one ${days}-day full access preview to use whenever you like`,
    description:
      'Unlocks Budgets, Doors, Seasons, two-level grouping, account linking, Attribution and full product editing. Start it when you have a buy worth looking at.',
    ...PREVIEW_CTA,
  },
  // Shown in the closing days of a preview. The deletion is irreversible, so the
  // warning is what stands between the user and losing the data.
  //
  // Design's wording says "trial" where the rest of Visual Assortment says
  // "preview" — kept as handed over, and raised with Product rather than
  // silently reconciled here.
  previewEnding: {
    daysLeft: (days: number) => `${days} days left on your trial`,
    oneDayLeft: '1 day left on your trial',
    lastDay: 'Last day of your trial',
    description:
      'Upgrade to Standard or Premium to avoid losing data entered into paid-tier fields.',
    upgrade: UPGRADE_NOW,
  },
  // Shown while a preview is running, so the end of the window is never a
  // surprise. Placeholder wording until Design and Product land on the real
  // presentation (sc-258611).
  previewIndicator: {
    daysLeft: (days: number) => `${days} days left`,
    oneDayLeft: '1 day left',
    endsToday: 'Ends today',
  },
  toolbar: {
    searchPlaceholder: 'Search',
    deselectAll: 'Deselect All',
    collapseAll: 'Collapse All',
    expandAll: 'Expand All',
    groupByLabel: 'Group by:',
    thenByLabel: 'Then by:',
    noneLabel: 'None',
    standardAttributesLabel: 'Standard Attributes',
    customAttributesLabel: 'Custom Attributes',
    setCustomAttributesCta: 'Set your custom attributes in settings',
    upgrade: 'Upgrade',
    unlock: 'Unlock',
    tooltips: {
      edit: 'Edit',
      delete: 'Delete From Assortment',
      toggleCardViewCompact: 'Hide Product Details',
      toggleCardViewExpanded: 'Show Product Details',
      groupBy: 'Group Products',
      filterBy: 'Needs Review',
    },
    filterBy: {
      cancelledProducts: 'Cancelled Products',
      unitMismatch: 'Unit Mismatches',
      unattributed: 'No Custom Attributes',
      notReceived: 'Not Received',
      missingInformation: {
        group: 'Missing Information',
        image: 'Image',
        brand: 'Brand',
        category: 'Category',
        color: 'Color',
        cost: 'Cost',
        deliveryMonth: 'Delivery Month',
        door: 'Door',
        price: 'Price',
        season: 'Season',
        wholesaleCurrency: 'Cost curr.',
        retailCurrency: 'Price curr.',
      },
    },
  },
  ordersTable: {
    loading: 'Loading orders...',
    error: 'Error loading orders',
    noOrders: 'No orders found',
    columnNames: {
      select: 'Select',
      poNumber: 'PO Number',
      brand: 'Brand',
      status: 'Status',
      linesheet: 'Linesheet',
      products: 'Products',
      units: 'Units',
      deliveryWindow: 'Delivery Window',
      createdOn: 'Created On',
      inAssortment: 'In Assortment',
      lastSent: 'Last Sent to Assortment',
    },
  },
  VAButton: {
    text: 'Send to Assortment',
    errorToast: {
      title: 'Error adding to assortment',
      description: 'There was an error adding products to your assortment.',
    },
    warningToast: {
      title: 'Adding to assortment',
      description: 'Your products are being synced into your assortment',
    },
  },
  syncToOrdersPanel: {
    title: 'Unit Mismatches',
    syncButton: 'Sync orders',
    syncingTitle: 'Syncing orders',
    successToast: {
      title: 'Orders synced',
      description: 'Your orders have been synced to your assortment.',
    },
  },
  deleteModal: {
    title: 'Delete products?',
    description:
      'Deleting products will remove them from your Visual Assortment, but they will not be removed from any existing JOOR orders. Once deleted, you can manually add products back to this view.',
    confirm: 'Confirm Delete',
    cancel: 'Cancel',
  },
  deleteAttributesModal: {
    setTitle: (attributeName: string) => `Delete ${attributeName}?`,
    description:
      'Deleting this optional category will remove it from all associated products. This action cannot be undone.',
    typeDescription:
      'Deleting this attribute will remove all defined values from associated products. This action cannot be undone.',
    confirm: 'Confirm Delete',
    cancel: 'Cancel',
    doNotShowAgain: "Don't show again",
  },
  cardPopup: {
    detailsAction: 'Details',
    deleteAction: 'Delete',
    goToOrderAction: 'Go to Order',
  },
  shareVAToAccountsModal: {
    title: 'Connected Accounts',
    subtitle:
      "You are associated with all of the accounts listed below. Please select the accounts from which you'd like to add products to your visual assortment.",
    save: 'Save',
  },
  noAccessPlaceholder: {
    title: 'Assortment not available',
    description:
      'This assortment belongs to an account you are not associated with. If you think this is an error, please contact the account owner.',
  },
  switchAccountModal: {
    title: 'Switch Accounts?',
    description:
      "Clicking Continue will seamlessly switch you to that account so you can view the order details. Once you're done, you can return to Visual Assortment in any of your linked accounts and pick up right where you left off.",
    continue: 'Continue',
    cancel: 'Cancel',
    doNotShowAgain: "Don't show again",
  },
  bulkEditPanel: {
    note: 'Note: You are editing all selected products to the same values.  Only some categories can be edited in bulk.',
    multipleColors: 'Multiple Colors',
    multipleDeliveryMonths: 'Multiple Delivery Months',
    multipleCategories: 'Multiple Categories',
    multipleDoors: 'Multiple Doors',
    multipleSeasons: 'Multiple Seasons',
    multipleCustomAttributes: 'Multiple Attrs',
    received: 'Mark as Received',
    toasts: {
      allSucceededTitle: 'Update successful',
      allSucceededDescription: 'All fields were updated successfully.',
      someSucceededDescription: (fields: string[]): string =>
        `Successfully updated: ${fields.join(', ')}.`,
      allFailedTitle: 'Update failed',
      allFailedDescription: 'None of the fields could be updated.',
      partialFailedTitle: 'Partial update',
      partialFailedDescription: (
        failed: string[],
        succeeded: string[],
      ): string => {
        const failedText = failed.join(', ')
        const succeededText = succeeded.join(', ')
        return `Failed to update: ${failedText}. Successfully updated: ${succeededText}.`
      },
      fields: {
        color: 'Color',
        deliveryMonth: 'Delivery Month',
        category: 'Category',
        season: 'Season',
        doors: 'Doors',
        received: 'Received',
      },
    },
  },
  addProductsPanel: {
    title: 'Add Products',
    selectJoorOrders: 'Select JOOR Orders',
    uploadOtherProducts: 'Upload Other Products',
  },
  addJoorProductsModal: {
    title: '+ Add Products: Select JOOR Orders',
    selectViewCriteria: {
      title: 'Select View Criteria',
      description:
        'Choose whether to organize by Order Creation Date or Delivery Window End and define a timeframe to see your orders.',
    },
    noOrders: 'No orders match the selected criteria.',
  },
  uploadProductsPanel: {
    title: 'Upload Products from non-JOOR orders',
    subtitle:
      'Note: This process is for importing products from non-JOOR orders managed elsewhere. For orders already in JOOR, add them directly via the Order List or Order Review.',
    downloadTemplate: {
      title: 'Download Template',
      placeholder: 'Download blank or existing',
      buttonText: 'Download',
      options: {
        blank: 'Blank template',
        addingNewProducts: 'For adding new products',
        existing: 'Existing template',
        updatingAndAddingProducts: 'For updating and adding products',
      },
    },
    uploadTemplate: {
      title: 'Upload Template',
      subtitle: 'Drag and drop or',
      selectFile: 'select file',
      maxFileSize: 'Max file size: 2MB',
      buttonText: 'Upload',
    },
    fileErrors: {
      tooManyFiles: 'Too many files',
      fileInvalidType: 'Invalid file type',
      fileTooLarge: 'File too large',
      description1: 'CSV, or XLSX, file size no more than 2MB.',
      description2: 'One file per upload.',
    },
  },
  editImageModal: {
    title: (productName: string) => `Edit Image | ${productName}`,
    uploadImage: {
      title: 'Upload New',
      subtitle: 'Drag and drop or',
      selectFile: 'select file',
      description1: 'JPG, JPEG, or PNG, file size no more than 2MB.',
      description2: 'Suggested size: Portrait 1130x1600px',
    },
    save: 'Save',
    fileErrors: {
      tooManyFiles: 'Too many files',
      fileInvalidType: 'Invalid file type',
      fileTooLarge: 'File too large',
    },
    toasts: {
      uploadSuccess: {
        title: 'Image uploaded',
        description: 'The product image has been updated successfully.',
      },
      uploadError: {
        title: 'Error uploading image',
        description: 'Failed to upload image. Please try again.',
      },
    },
  },
  settingsSidePanel: {
    title: 'Visual Assortment Settings',
    closeTooltip: 'Resolve highlighted areas to proceed',
    backToBudget: 'Back to budget',
    yourPlan: 'Your Plan',
    accountsAndUsers: 'Accounts and Users',
    optionalAttributes: 'Optional Attributes',
    settings: 'Settings',
    settingsDisabled: 'Add and sync your first products to unlock Settings',
    saving: 'Saving Settings',
    saved: 'Settings Saved',
    planUpdatedPrefix: 'Plan Updated:',
    planUpdatedDescription:
      'Adjust the highlighted areas below to match your current package limits. These must be resolved before proceeding to Visual Assortment.',
    limitsResolvedPrefix: 'Plan Updated:',
    limitsResolvedDescription: 'Settings adjusted.',
    content: {
      connectedAccounts: 'Connected Accounts',
      users: 'Users',
      userLimitsDescription: (limit: number) =>
        `Designate which of your accounts users can access Visual Assortment. Your plan allows for up to ${limit}. Need more users?`,
      upgradeToPremium: UPGRADE_TO.premium,
      upgradeNow: UPGRADE_NOW,
      upgradeToStandard: UPGRADE_TO.standard,
      attributesLimitsDescription: (attributeName: string, limit: number) =>
        `Define ${lowerFirstLetter(attributeName)} to assign to your products. Your plan allows for up to ${limit} ${lowerFirstLetter(attributeName)}. Need more ${lowerFirstLetter(attributeName)}?`,
      contactUs: 'Contact us.',
      contactUsForUpgrade: 'Contact us for upgrade options',
      upgradeHere: 'Upgrade here.',
      usersOverLimitMessage:
        'Please review your users and delete any that exceed your plan limits.',
      attributeNoAccessMessage: (attributeName: string) =>
        `You no longer have access to ${lowerFirstLetter(attributeName)}.`,
      attributeOverLimitMessage: (attributeName: string) =>
        `Please review your ${lowerFirstLetter(attributeName)} and delete any that exceed your plan limits.`,
      connectedAccountsDescription:
        'Combine items from associated accounts into the same visual assortment.',
      ownerAccountSwitchTooltip:
        'This account manages your Visual Assortment subscription and cannot be disconnected.',
      currentLoggedAccountSwitchTooltip:
        'This is your current logged-in account and cannot be disconnected.',
      valueCannotBeEmptyTitle: 'Value cannot be empty',
      maxCharacterLimitExceeded: (max: number) =>
        `Value cannot exceed ${max} characters.`,
      valueCannotBeDuplicated:
        'This value is already in use. Please enter a unique one.',
      valueCannotBeEmptyDescription: 'Use the delete button to remove a value.',
      definedAttributes: (attributeName: string) =>
        `Define ${lowerFirstLetter(attributeName)} to assign to your products.`,
      customAttributesLimitDescription: (used: number, max: number) =>
        `Your plan allows for up to ${max} custom attributes. You are using ${used} out of ${max}. Need more attributes?`,
      customAttributesHeader: 'Custom Attributes',
      lockedAttributesHeader: 'Doors & Custom Attributes',
      lockedAttributesDescription:
        'Define doors and/or custom attributes to assign to your products.',
      attributesNoAccessMessage:
        'You no longer have access to these attributes. Please review and delete any values that exceed your plan limits.',
      addCustomAttribute: '+ Add Custom Attribute',
      maxCustomAttributesReachedMessage:
        'Maximum Attributes Added. Need more attributes?',
      contactUsLink: 'Contact us',
      addAttributeValue: '+ Add Value',
      maxAttributeValuesReached: 'Maximum Values Added',
      customAttributeNamePlaceholder: 'Custom Attribute',
      customAttributeValuePlaceholder: 'Define Value',
    },
    errors: {
      createAttributeValues: {
        title: 'Create attribute values failed',
        description:
          'An error occurred while creating attribute values. Please try again later.',
      },
      updateAttributeValues: {
        title: 'Update attribute values failed',
        description:
          'An error occurred while updating attribute values. Please try again later.',
      },
      deleteAttributeValues: {
        title: 'Delete attribute values failed',
        description:
          'An error occurred while deleting attribute values. Please try again later.',
      },
      createAttributeKey: {
        title: 'Create custom attribute failed',
        description:
          'An error occurred while creating the custom attribute. Please try again later.',
      },
      updateAttributeKey: {
        title: 'Update custom attribute failed',
        description:
          'An error occurred while updating the custom attribute. Please try again later.',
      },
      deleteAttributeKey: {
        title: 'Delete custom attribute failed',
        description:
          'An error occurred while deleting the custom attribute. Please try again later.',
      },
    },
  },
  noAccessLayouts: {
    notPurchasedOrLapsed: {
      title: 'See the whole picture with Visual Assortment',
      description:
        'Visual Assortment is the best way to manage your buys. Stop piecing together spreadsheets and start seeing all your products in one high-level view—including the ones ordered outside JOOR.',
      button: 'View Plans to Unlock Today',
      linkLabel: 'Learn more about Visual Assortment',
    },
    noUserAccess: {
      title: 'Unlock Visual Assortment',
      setDescriptionWithUsers: (
        usersLimit: number,
        userDisplayEmailList: string[],
      ) =>
        `Your account currently includes ${usersLimit} designated users. The current seat holder${userDisplayEmailList.length > 1 ? 's' : ''} for your account ${userDisplayEmailList.length > 1 ? 'are' : 'is'} ${userDisplayEmailList.join(', ')}. To be added to this feature, please reach out to them.`,
      needMoreUsers: 'Need more users?',
    },
    noActiveUsersAccess: {
      title: 'Unlock Visual Assortment',
      description:
        'Your account currently includes Visual Assortment, but has no users designated to access it. ',
    },
    imageAlt: 'visual assortment layout sample',
    upgradeHereLinkLabel: 'Upgrade here',
    contactUsLinkLabel: 'Contact us',
  },
  confirmResetModal: {
    title: 'Reset Configuration?',
    description:
      'Resetting configuration will erase all entered budget data. Do you want to proceed?',
    cancel: 'Cancel',
    confirm: 'Continue',
  },
  budgetsSidePanel: {
    title: 'Budgets',
    loading: 'Loading...',
    creating: 'Creating your budget',
    saving: 'Saving your configuration',
    savingBudget: 'Saving budget',
    error: 'Something went wrong',
    configuration: {
      title: 'Configuration',
      saveButton: 'Save Configuration',
      cancelButton: 'Cancel',
      defineLevels: {
        title: 'Define levels',
        description:
          'You can choose up to two levels to organize your budget by.',
        groupByLabel: 'Group by:',
        thenByLabel: 'Then by:',
        placeholder: 'Select level',
      },
      currency: {
        title: 'Primary Currency',
        description:
          'All product prices will be converted to your primary currency for consistent budget tracking.',
        dropdownLabel: 'Currency:',
        placeholder: 'Select currency',
        warningDescription:
          'Currency conversions are estimates based on current market rates and may not reflect the exact exchange rate at the time of transaction.',
      },
      season: {
        title: 'Season',
        withSeasonsDescription: 'Select one of your seasons.',
        noSeasonsDescription: 'You have no seasons defined.',
        allProductsPill: 'All Products',
        noSeasonsWarningTitle: 'Is your budget specific to a season?',
        noSeasonsWarningLink: 'Configure seasons now',
        noSeasonsWarningSuffix: ' to avoid needing to reset your budget later.',
      },
    },
    budget: {
      title: 'Budget',
      modifyConfigButton: 'Modify Configuration',
      budgetDetails: {
        title: 'Budget Details',
        downloadBudget: 'Download Budget',
        csvOptionLabel: 'CSV (.csv)',
        excelOptionLabel: 'Excel (.xlsx)',
        errorDownLoad: {
          title: 'Download Budget failed',
          description: 'Error downloading budget. Please try again later.',
        },
      },
      missingInfoBanner: {
        title: 'Missing Product Information:',
        description01:
          'Some products in your Visual Assortment are missing values required for accurate budget calculations.',
        description02: 'Filter your view by ',
        description03: ' to find and update the missing data.',
        or: ' or ',
      },
      budgetTable: {
        totalRowLabel: 'Total',
        errorLoading: 'Could not load budget data. Try again later.',
        columns: {
          group: 'Group',
          budgetCost: 'Budget Cost',
          actualCost: 'Actual Cost',
          percentCostSpent: '% Cost Spent',
          budgetRetail: 'Budget Retail',
          actualRetail: 'Actual Retail',
          percentRetailSpent: '% Retail Spent',
          margin: 'Margin',
        },
        overBudgetWarning: {
          title: 'Over-Allocated Budget:',
          description:
            'The sum of your sub-level values exceeds the total entered budget for the main level.',
        },
        emptyBudgetBanner: {
          title: 'No data in this budget yet',
          forSeason: (seasonName: string) =>
            `Add products with the “${seasonName}” season to your assortment to see them in this budget.`,
          forAllProducts:
            'Add products to your assortment to see them in this budget.',
        },
        emptyAllProducts: {
          title: 'No products',
          description: 'products to your assortment to populate your budget.',
          actionLabel: 'Add',
        },
        emptySeason: {
          title: (seasonName: string) => `No products in "${seasonName}"`,
          description: 'season to products to populate your budget.',
          actionLabel: 'Assign',
        },
      },
    },
    errors: {
      createBudget: {
        title: 'Create budget failed',
        description:
          'An error occurred while creating the budget. Please try again later.',
      },
      updateBudget: {
        title: 'Update budget failed',
        description:
          'An error occurred while updating the budget. Please try again later.',
      },
      upsertBudgetRows: {
        title: 'Update budget failed',
        description:
          'An error occurred while saving the budget amount. Please try again later.',
      },
    },
    budgetSeasonDeleteWarning: {
      title: 'Delete season and reconfigure budget?',
      body: 'Deleting this season will remove it from all associated products and expand your budget to include everything in your assortment. This action cannot be undone.',
      confirm: 'Delete',
      cancel: 'Cancel',
      downloadCsv: 'Download CSV',
      downloadExcel: 'Download Excel',
      downloadHint: 'Save your current budget before deleting season:',
    },
    budgetGroupingDeleteWarning: {
      title: (attributeName: string) =>
        `Delete ${attributeName} and reconfigure budget?`,
      body: 'Deleting this attribute will remove it from all associated products and remove it as a level in your budget. This action cannot be undone.',
      confirm: 'Delete',
      cancel: 'Cancel',
      downloadCsv: 'Download CSV',
      downloadExcel: 'Download Excel',
      downloadHint: 'Save your current budget before deleting this attribute:',
    },
  },
}
